import { yupResolver } from '@hookform/resolvers/yup';
import { Chip, FormControl, Grid, InputLabel, MenuItem, Select } from '@mui/material';
import { addDays } from 'date-fns';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import * as yup from 'yup';
import { MixedSchema } from 'yup/lib/mixed';
import {
    FormAutocomplete,
    FormCheckbox,
    FormDatePicker,
    FormKeyboardDateTimePicker,
    FormSelect,
    FormTextField
} from '../../common/form';
import {
    AgreementType,
    CreateAgreementDto,
    CustomerConnectionType,
    CustomerType
} from '../../common/PricePrgClient';
import { RootState } from '../../store';
import { toDateString } from '../../utils';
import CustomerGroupSelector from '../customer/CustomerGroupSelector';
import CustomerSelector from '../customer/CustomerSelector';
import { getAvailableUserGroups } from '../usergroup/redux/userGroupSelectors';
import { getAppSettings } from '../settings/redux/settingsSelectors';

const nameMaxLength = 60;

type OwnProps = {
    defaultValues: CreateAgreementDto;
    submitForm(form: CreateAgreementDto): void;
};

const mapStateToProps = (state: RootState) => ({
    userGroups: getAvailableUserGroups(state),
    currencies: getAppSettings(state).availableCurrencies,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export type AgreementFormModel = Omit<CreateAgreementDto, 'validFrom' | 'validTo' | 'preorderDate'> & {
    validFrom: Date;
    validTo: Date;
    preorderDate?: Date;
};

const schema: yup.SchemaOf<AgreementFormModel> = yup.object().shape({
    name: yup.string().required().max(nameMaxLength).label('Name'),
    description: yup.string().label('Description'),
    currency: yup.string().required().label('Currency'),
    customerConnectionType: yup.number().required().label('Customer Connection'),
    customerGroupId: yup
        .mixed()
        .when('customerConnectionType', {
            is: CustomerConnectionType.CustomerGroup,
            then: yup.mixed().required(),
            otherwise: yup.mixed().nullable()
        })
        .label('Customer group id'),
    customerId: yup
        .mixed()
        .when('customerConnectionType', (cct: CustomerConnectionType, ms: MixedSchema) =>
            [CustomerConnectionType.BusinessChain, CustomerConnectionType.Customer].includes(cct)
                ? ms.required()
                : ms
        )
        .label('Customer id'),
    handleExternalPrices: yup.boolean().required().label('Handle external prices'),
    agreementType: yup
        .mixed<AgreementType>()
        .oneOf([AgreementType.Regular, AgreementType.Preorder, AgreementType.Warchest])
        .required()
        .label('Agreement type'),
    preorderDate: yup
        .date()
        .notRequired()
        .transform((curr, orig) => (orig === '' ? undefined : curr))
        .when('agreementType', {
            is: AgreementType.Preorder,
            then: yup
                .date()
                .min(addDays(new Date(), -1), "Preorder date can't be before today")
                .required('Preorder date is required'),
            otherwise: yup.date().nullable()
        })
        .label('Preorder date'),
    validFrom: yup
        .date()
        .required()
        .min(addDays(new Date(), -1), "Valid from can't be before today")
        .label('Valid from'),
    validTo: yup
        .date()
        .required()
        .min(yup.ref('validFrom'), "Valid to can't be before Valid from")
        .label('Valid to'),
    permittedUserGroups: yup.array().default([])
});

const AgreementForm = ({ defaultValues, submitForm, userGroups, currencies }: Props) => {
    const { control, handleSubmit, getValues, watch } = useForm<AgreementFormModel>({
        defaultValues: {
            ...defaultValues,
            validFrom: new Date(defaultValues.validFrom),
            validTo: new Date(defaultValues.validTo),
            preorderDate: undefined
        },
        resolver: yupResolver(schema)
    });

    // Calling watch in order to cause a re-render when Preorder agreement checkbox is selected
    watch();
    const [isPreOrderDateVisible, setIsPreOrderDateVisible] = useState(false);
    // Callback version of watch.  It's your responsibility to unsubscribe when done.
    useEffect(() => {
        const subscription = watch(value =>
            setIsPreOrderDateVisible(value?.agreementType == AgreementType.Preorder)
        );
        return () => subscription.unsubscribe();
    }, [watch]);
    const currentCustomerConnectionType = getValues('customerConnectionType');

    const getName = (userGroupId: number) =>
        userGroups.find(ug => ug.userGroupId === userGroupId)?.name ?? '??';

    return (
        <form
            id="agreement-form"
            onSubmit={handleSubmit(({ validFrom, validTo, preorderDate, ...rest }) => {
                submitForm({
                    ...rest,
                    validFrom: toDateString(validFrom),
                    validTo: toDateString(validTo),
                    preorderDate:
                        rest.agreementType === AgreementType.Preorder && preorderDate != null
                            ? preorderDate.toISOString()
                            : undefined
                });
            })}
        >
            <Grid container spacing={2} direction="column" mt={1}>
                <Grid item>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <FormTextField autoFocus label="Name" name="name" control={control} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormTextField label="Description" name="description" control={control} />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <FormSelect label="Currency" name="currency" control={control}>
                                {currencies.map(cur => (
                                    <MenuItem key={cur} value={cur}>
                                        {cur}
                                    </MenuItem>
                                ))}
                            </FormSelect>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <FormDatePicker
                                name="validFrom"
                                label="Valid from"
                                control={control}
                                inputFormat="yyyy-MM-dd"
                                disablePast={true}
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormDatePicker
                                name="validTo"
                                label="Valid to"
                                control={control}
                                inputFormat="yyyy-MM-dd"
                                disablePast={true}
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <FormControl sx={{ width: '75%' }} fullWidth>
                                <InputLabel id="customerConnectionTypeLabel">
                                    Customer connection type
                                </InputLabel>
                                <Controller
                                    control={control}
                                    name="customerConnectionType"
                                    render={({ field, fieldState: { error } }) => (
                                        <Select
                                            labelId="customerConnectionTypeLabel"
                                            {...field}
                                            label="Customer connection type"
                                            error={!!error}
                                        >
                                            <MenuItem
                                                key={CustomerConnectionType.Customer}
                                                value={CustomerConnectionType.Customer}
                                            >
                                                Customer
                                            </MenuItem>
                                            <MenuItem
                                                key={CustomerConnectionType.BusinessChain}
                                                value={CustomerConnectionType.BusinessChain}
                                            >
                                                Business chain
                                            </MenuItem>
                                            <MenuItem
                                                key={CustomerConnectionType.CustomerGroup}
                                                value={CustomerConnectionType.CustomerGroup}
                                            >
                                                Customer group
                                            </MenuItem>
                                        </Select>
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        {currentCustomerConnectionType === CustomerConnectionType.Customer && (
                            <Grid item xs={12} md={5}>
                                <Controller
                                    name="customerId"
                                    control={control}
                                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                                        <CustomerSelector
                                            selectedCustomer={value}
                                            onSelectCustomer={onChange}
                                            label="Customer"
                                            showCustomerType={CustomerType.NormalCustomer}
                                            helperText={error?.message}
                                            error={!!error}
                                        />
                                    )}
                                />
                            </Grid>
                        )}
                        {currentCustomerConnectionType === CustomerConnectionType.BusinessChain && (
                            <Grid item xs={12} md={5}>
                                <Controller
                                    name="customerId"
                                    control={control}
                                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                                        <CustomerSelector
                                            selectedCustomer={value}
                                            onSelectCustomer={onChange}
                                            label="Business chain"
                                            showCustomerType={CustomerType.HQ}
                                            helperText={error?.message}
                                            error={!!error}
                                        />
                                    )}
                                />
                            </Grid>
                        )}
                        {currentCustomerConnectionType === CustomerConnectionType.CustomerGroup && (
                            <Grid item xs={12} md={5}>
                                <Controller
                                    name="customerGroupId"
                                    control={control}
                                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                                        <CustomerGroupSelector
                                            selectedCustomerGroup={value}
                                            onSelectCustomerGroup={onChange}
                                            label="Customer group"
                                            helperText={error?.message}
                                            error={!!error}
                                        />
                                    )}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Grid>
                <Grid item xs={12} md={5}>
                    <FormCheckbox
                        label="Handle external prices"
                        name="handleExternalPrices"
                        control={control}
                    />
                </Grid>
                <Grid item xs={12} md={5}>
                    <FormAutocomplete
                        control={control}
                        multiple={true}
                        name="permittedUserGroups"
                        options={userGroups.map(c => c.userGroupId)}
                        label="Permitted user groups"
                        getOptionLabel={userGroupId =>
                            userGroups.find(c => c.userGroupId === userGroupId)?.name ?? '??'
                        }
                        isOptionEqualToValue={(options, values) => options === values}
                        renderTags={(tagValue, getTagProps) =>
                            tagValue
                                .sort((a, b) => getName(a).localeCompare(getName(b)))
                                .map((option, index) => (
                                    <Chip label={getName(option)} {...getTagProps({ index })} key={option} />
                                ))
                        }
                    />
                </Grid>
                <Grid item>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                            <FormSelect label="Agreement type" name="agreementType" control={control}>
                                {Object.keys(AgreementType)
                                    .map(x => Number(x))
                                    .filter(x => !isNaN(x))
                                    .map(idx => [idx, AgreementType[idx] as string] as const)
                                    .map(([idx, name]) => (
                                        <MenuItem
                                            key={idx}
                                            value={idx}
                                            disabled={idx === AgreementType.Warchest}
                                        >
                                            {name}
                                        </MenuItem>
                                    ))}
                            </FormSelect>
                        </Grid>

                        {isPreOrderDateVisible && (
                            <Grid item xs={12} md={3}>
                                <FormKeyboardDateTimePicker
                                    name="preorderDate"
                                    label="Preorder date"
                                    inputFormat="yyyy-MM-dd HH:mm"
                                    control={control}
                                    disablePast={true}
                                    InputProps={{ readOnly: true }}
                                    ampm={false}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </form>
    );
};
export default connect(mapStateToProps)(AgreementForm);
